import { useAuthReady } from './useAuthReady';
import { useQuery, useQueries, keepPreviousData } from '@tanstack/react-query';
import { api } from '../lib/api';
import { MARKET_IDS } from '@incpt/shared';
import type { Coin, CoinSymbol } from '../components/wallet/TokenSheet';

// CoinGecko ids, from the one table both sides share (@incpt/shared
// MARKET_IDS). It also carries each coin's Binance symbol, which is what the
// API charts from — keeping them together is what stops a new coin shipping
// with a working price and an empty chart, as Dogecoin and XRP did.
const CG_ID: Record<CoinSymbol, string> = Object.fromEntries(
  Object.entries(MARKET_IDS).map(([symbol, ids]) => [symbol, ids.coingeckoId]),
) as Record<CoinSymbol, string>;

const ALL_SYMBOLS = Object.keys(CG_ID) as CoinSymbol[];

interface MarketRow {
  current_price: number | null;
  price_change_percentage_24h: number | null;
  market_cap: number | null;
  total_volume: number | null;
  last_updated?: string;
}

/** One OHLC candle: [timestamp, open, high, low, close]. */
export type Candle = [number, number, number, number, number];
export type Timeframe = '1d' | '7d' | '1m' | '1y' | 'all';

/** Spot + 24h change for every supported symbol (no candles — for asset lists). */
export function useMarkets() {
  const { canFetch: ready, initData } = useAuthReady();

  const queries = useQueries({
    queries: ALL_SYMBOLS.map((symbol) => {
      const id = CG_ID[symbol];
      return {
        queryKey: ['market', id],
        enabled: ready,
        staleTime: 60_000,
        queryFn: async ({ client, queryKey }): Promise<MarketRow | null> => {
          const res = await api<{ markets: MarketRow[] }>(initData, `/v1/market?ids=${id}`);
          const row = res.markets[0] ?? null;
          if (row && (row.current_price != null || row.market_cap != null)) return row;
          const prev = client.getQueryData<MarketRow | null>(queryKey);
          if (prev && (prev.current_price != null || prev.market_cap != null)) return prev;
          return row;
        },
      };
    }),
  });

  const bySymbol = ALL_SYMBOLS.reduce<
    Record<CoinSymbol, { price: number | null; change24h: number | null }>
  >(
    (acc, symbol, i) => {
      const row = queries[i]?.data;
      acc[symbol] = {
        price: row?.current_price ?? null,
        change24h: row?.price_change_percentage_24h ?? null,
      };
      return acc;
    },
    {} as Record<CoinSymbol, { price: number | null; change24h: number | null }>,
  );

  return { bySymbol };
}

/** Live price / 24h change / market stats + OHLC candles for the timeframe. */
export function useCoinMarket(coin: Coin, tf: Timeframe) {
  const { canFetch: ready, initData } = useAuthReady();
  const id = CG_ID[coin.symbol];

  const market = useQuery({
    queryKey: ['market', id],
    enabled: ready,
    staleTime: 60_000,
    // Keep last good price/stats on screen while a refetch is in flight / fails.
    placeholderData: keepPreviousData,
    queryFn: async ({ client, queryKey }): Promise<MarketRow | null> => {
      const res = await api<{ markets: MarketRow[] }>(initData, `/v1/market?ids=${id}`);
      const row = res.markets[0] ?? null;
      // API may 200 with all-nulls during an outage — don't wipe a good cache.
      if (row && (row.current_price != null || row.market_cap != null)) return row;
      const prev = client.getQueryData<MarketRow | null>(queryKey);
      if (prev && (prev.current_price != null || prev.market_cap != null)) return prev;
      return row;
    },
  });

  const candles = useQuery({
    queryKey: ['market-candles', id, tf],
    enabled: ready,
    staleTime: 60_000,
    // Keep the previous timeframe's chart on screen while the new one loads,
    // and keep last-good candles if the refetch comes back empty.
    placeholderData: keepPreviousData,
    queryFn: async ({ client, queryKey }): Promise<Candle[]> => {
      const res = await api<{ candles?: Candle[] }>(initData, `/v1/market/candles?id=${id}&tf=${tf}`);
      const next = res.candles ?? [];
      if (next.length >= 2) return next;
      const prev = client.getQueryData<Candle[]>(queryKey);
      if (prev && prev.length >= 2) return prev;
      return next;
    },
  });

  return {
    price: market.data?.current_price ?? null,
    change24h: market.data?.price_change_percentage_24h ?? null,
    marketCap: market.data?.market_cap ?? null,
    volume: market.data?.total_volume ?? null,
    lastUpdated: market.data?.last_updated ?? null,
    candles: candles.data ?? [],
    chartLoading: candles.isFetching,
    loading: market.isLoading || candles.isLoading,
  };
}
